import { useCallback, useEffect, useRef, useState } from 'react'
import { firstPreShowStep, nextPreShowStep, SETTLE_MS, type PreShowStep } from './preShow'
import type { EventPhase } from './rules'

/**
 * Where the pre-show is (`PreShowStep`), moved on as the room connects and something goes live. `finish` ends it once
 * its wave has opened the page.
 */
export function usePreShowStep({
  eligible,
  phase,
  live,
  connected,
}: {
  eligible: boolean
  phase: EventPhase
  live: boolean
  connected: boolean
}): [PreShowStep, () => void] {
  const [step, setStep] = useState<PreShowStep>(() => firstPreShowStep({ eligible, phase }))
  const connectedAt = useRef<number | null>(null)
  const [, setCheck] = useState(0)
  if (connected && connectedAt.current === null) connectedAt.current = Date.now()
  if (!connected) connectedAt.current = null
  const settledFor = connectedAt.current === null ? null : Date.now() - connectedAt.current

  const next = nextPreShowStep(step, { eligible, live, settledFor })
  useEffect(() => {
    if (next !== step) setStep(next)
  }, [next, step])

  // Looks again once the room has been connected long enough to have said whether anything is live.
  const waitMs = settledFor !== null && settledFor < SETTLE_MS && (step === 'deciding' || step === 'waiting') ? SETTLE_MS - settledFor : null
  useEffect(() => {
    if (waitMs === null) return
    const timer = window.setTimeout(() => setCheck((n) => n + 1), waitMs + 10)
    return () => window.clearTimeout(timer)
  }, [waitMs])

  const finish = useCallback(() => setStep('done'), [])
  return [step, finish]
}
